// Thin fetch wrapper for the /api/* Pages Functions endpoints.
// We always include credentials so the session cookie travels with requests.

export type Model = {
  id: string;
  name: string;
  description: string | null;
  r2_key: string;
  file_size: number;
  preview_key?: string | null;
  preview_updated_at?: number | null;
  created_at: number;
  updated_at: number;
};

export type Label = {
  id: string;
  text: string;
  color: string;
  position_x: number;
  position_y: number;
  position_z: number;
};

export type Question = {
  id: string;
  question_type: "mcq" | "text";
  prompt: string;
  options_json: string | null;
  correct_answer: string;
  position_x: number;
  position_y: number;
  position_z: number;
  camera_pos_x: number | null;
  camera_pos_y: number | null;
  camera_pos_z: number | null;
  camera_target_x: number | null;
  camera_target_y: number | null;
  camera_target_z: number | null;
  sort_order: number;
};

export type ModelDetail = {
  model: Model;
  labels: Label[];
  questions: Question[];
};

/** Several individually-scanned models composed into one positioned scene. */
export type Assembly = {
  id: string;
  name: string;
  description: string | null;
  /** Explode travel at t=1, as a multiple of the assembly's bounding radius. */
  explode_distance: number;
  /** Restructure grading tolerance, as a fraction of the bounding radius. */
  snap_tolerance: number;
  preview_key?: string | null;
  preview_updated_at?: number | null;
  part_count?: number;
  created_at: number;
  updated_at: number;
};

export type AssemblyPart = {
  id: string;
  model_id: string;
  name: string;
  pos_x: number;
  pos_y: number;
  pos_z: number;
  rot_x: number;
  rot_y: number;
  rot_z: number;
  scale: number;
  /** null → derive the explode direction radially from the assembly centre. */
  explode_x: number | null;
  explode_y: number | null;
  explode_z: number | null;
  /** SQLite has no bool; these are 0/1. */
  is_base: number;
  locked: number;
  sort_order: number;
  file_size: number;
  model_name: string;
};

export type AssemblyDetail = {
  assembly: Assembly;
  parts: AssemblyPart[];
  references: AssemblyReference[];
};

export type AssemblyReference = {
  id: string;
  title: string;
  caption: string;
  kind: "image" | "pdf" | "link";
  url: string | null;
  content_type: string | null;
  file_size: number;
  created_at: number;
};

/** Shape accepted by the bulk part save. Every field but `id` is optional. */
export type PartPatch = {
  id: string;
  name?: string;
  pos?: [number, number, number];
  rot?: [number, number, number];
  scale?: number;
  explode?: [number, number, number] | null;
  is_base?: boolean;
  locked?: boolean;
  sort_order?: number;
};

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, body || res.statusText);
  }
  return (await res.json()) as T;
}

export const api = {
  async health() {
    return request<{ ok: boolean }>("/health");
  },
  async login(email: string, password: string) {
    return request<{ ok: true }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  },
  async logout() {
    return request<{ ok: true }>("/auth/logout", { method: "POST" });
  },
  async me() {
    return request<{ user: { id: string; email: string; display_name: string | null; role: "admin" | "student" } | null }>(
      "/auth/me",
    ).catch(() => ({ user: null }));
  },
  async listModels() {
    return request<{ models: Model[] }>("/models");
  },
  async getModel(id: string) {
    return request<ModelDetail>(`/models/${id}`);
  },
  modelFileUrl(id: string) {
    return `/api/models/${id}/file`;
  },
  /**
   * URL for the preview thumbnail. Returns `null` if the model has no preview yet
   * so the UI can render a fallback. The `v` query param busts the immutable
   * cache when the preview is re-captured.
   */
  modelPreviewUrl(model: Pick<Model, "id" | "preview_key" | "preview_updated_at">): string | null {
    if (!model.preview_key) return null;
    const v = model.preview_updated_at ?? 0;
    return `/api/models/${model.id}/preview?v=${v}`;
  },
  async uploadModelPreview(id: string, blob: Blob) {
    const res = await fetch(`/api/models/${id}/preview`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "image/png" },
      body: blob,
    });
    if (!res.ok) throw new ApiError(res.status, await res.text());
    return (await res.json()) as {
      ok: true;
      preview_key: string;
      preview_updated_at: number;
    };
  },
  async uploadModel(name: string, description: string, file: File) {
    const form = new FormData();
    form.append("name", name);
    form.append("description", description);
    form.append("file", file);
    const res = await fetch(`/api/models`, {
      method: "POST",
      credentials: "include",
      body: form,
    });
    if (!res.ok) throw new ApiError(res.status, await res.text());
    return (await res.json()) as { id: string; name: string };
  },
  async deleteModel(id: string) {
    return request<{ ok: true }>(`/models/${id}`, { method: "DELETE" });
  },
  async createLabel(
    modelId: string,
    body: { text: string; color: string; position: [number, number, number] },
  ) {
    return request<{ id: string }>(`/models/${modelId}/labels`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async deleteLabel(id: string) {
    return request<{ ok: true }>(`/labels/${id}`, { method: "DELETE" });
  },

  /* ----- Assemblies ---------------------------------------------------- */

  async listAssemblies() {
    return request<{ assemblies: Assembly[] }>("/assemblies");
  },
  async getAssembly(id: string) {
    return request<AssemblyDetail>(`/assemblies/${id}`);
  },
  async createAssembly(name: string, description = "") {
    return request<{ id: string; name: string }>("/assemblies", {
      method: "POST",
      body: JSON.stringify({ name, description }),
    });
  },
  async updateAssembly(
    id: string,
    body: Partial<Pick<Assembly, "name" | "description" | "explode_distance" | "snap_tolerance">>,
  ) {
    return request<{ ok: true }>(`/assemblies/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  async deleteAssembly(id: string) {
    return request<{ ok: true }>(`/assemblies/${id}`, { method: "DELETE" });
  },
  async addAssemblyPart(assemblyId: string, body: { model_id: string; name?: string; is_base?: boolean }) {
    return request<{ id: string }>(`/assemblies/${assemblyId}/parts`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  /** Bulk-save part transforms. One request so a save is atomic to the user. */
  async saveAssemblyParts(assemblyId: string, parts: PartPatch[]) {
    return request<{ ok: true; updated: number }>(`/assemblies/${assemblyId}/parts`, {
      method: "PUT",
      body: JSON.stringify({ parts }),
    });
  },
  async deleteAssemblyPart(partId: string) {
    return request<{ ok: true }>(`/parts/${partId}`, { method: "DELETE" });
  },
  assemblyPreviewUrl(
    a: Pick<Assembly, "id" | "preview_key" | "preview_updated_at">,
  ): string | null {
    if (!a.preview_key) return null;
    return `/api/assemblies/${a.id}/preview?v=${a.preview_updated_at ?? 0}`;
  },
  async uploadAssemblyPreview(id: string, blob: Blob) {
    const res = await fetch(`/api/assemblies/${id}/preview`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "image/png" },
      body: blob,
    });
    if (!res.ok) throw new ApiError(res.status, await res.text());
    return (await res.json()) as { ok: true; preview_key: string; preview_updated_at: number };
  },
  referenceFileUrl(id: string) { return `/api/references/${id}/file`; },
  async addReference(assemblyId: string, title: string, caption: string, source: File | string) {
    if (typeof source === "string") return request<{ id: string }>(`/assemblies/${assemblyId}/references`, {
      method: "POST", body: JSON.stringify({ title, caption, url: source }),
    });
    const form = new FormData();
    form.set("title", title);
    form.set("caption", caption);
    form.set("file", source);
    const res = await fetch(`/api/assemblies/${assemblyId}/references`, {
      method: "POST", credentials: "include", body: form,
    });
    if (!res.ok) throw new ApiError(res.status, await res.text());
    return await res.json() as { id: string };
  },
  deleteReference(id: string) {
    return request<{ ok: true }>(`/references/${id}`, { method: "DELETE" });
  },
};

export { ApiError };
